import React, { useEffect, useMemo, useState } from 'react';
import {
  Building2, Search, Plus, Trash2, 
  Save, UserCog, GripVertical, AlertCircle, Mail, Phone,
  User, FolderPlus, Lock, Users, Briefcase, ChevronRight, Layout, BadgeCheck, X, ShieldCheck, Settings, ArrowLeft
} from 'lucide-react';

// const API_BASE_URL = 'http://localhost:8000';
// API 통합관리
import { API_BASE_URL } from '../../config';

// 🚨 [추가] 아까 만든 권한 매트릭스 컴포넌트 불러오기 (경로는 폴더 구조에 맞게 수정해주세요)
import AdminSettings from './AdminSettings';

// =============================================================================
// 📋 [SECTION 1] 타입 정의 (Types)
// =============================================================================
type Team = { id: number; name: string; };
type Account = {
  id: number;
  name: string;
  username: string;
  email: string;
  phone: string | null;
  team_id: number | null;
  team_name?: string | null;
  grade?: string;
  role?: string; // 🚨 [신규 추가] 권한 필드
};

interface AccountManagementProps {
  // 🚨 [신규 추가] 에러 방지용 통로 뚫기
  globalRefreshKey?: number; 
}

const AccountManagement: React.FC<AccountManagementProps> = ({ globalRefreshKey = 0 }) => {
  
  // 🚨 [핵심 신규 상태] 상단 탭 대신, 매트릭스 화면을 덮어씌울 상태값
  const [showMatrix, setShowMatrix] = useState(false);

  // =============================================================================
  // ⚙️ [SECTION 2] 상태 관리 (State Management)
  // =============================================================================
  const [teams, setTeams] = useState<Team[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [grades, setGrades] = useState<string[]>([]); 
  const [loading, setLoading] = useState(false);
  
  const [topSearchTerm, setTopSearchTerm] = useState('');
  const [bottomSearchTerm, setBottomSearchTerm] = useState('');
  
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());
  const [selectedTeamId, setSelectedTeamId] = useState<number | 'unassigned' | 'all'>('all');
  const [draggedUserId, setDraggedUserId] = useState<number | null>(null);
  
  const [isAddingTeam, setIsAddingTeam] = useState(false);
  const [isManagingGrades, setIsManagingGrades] = useState(false); 
  const [isAddingUser, setIsAddingUser] = useState(false); // 👤 신규 계정 생성 모달 상태
  const [newTeamName, setNewTeamName] = useState('');
  const [newGradeName, setNewGradeName] = useState(''); 
  const [newPassword, setNewPassword] = useState('');
  
  // 💾 신규 계정 입력을 위한 임시 상태 (role 기본값 추가)
  const [newUser, setNewUser] = useState({ 
    name: '', username: '', email: '', password: '', 
    team_id: null as number | null, grade: '사원', role: 'user' 
  });

  // 💾 [핵심] 상세 패널 수정용 임시 상태 (저장 전까지 목록의 텍스트가 바뀌지 않게 함)
  const [editingUser, setEditingUser] = useState<Account | null>(null);

  // =============================================================================
  // 🔄 [SECTION 3] 데이터 로드 (Data Fetching)
  // =============================================================================
  const loadData = async () => {
    setLoading(true);
    try {
      const [tRes, uRes, gRes] = await Promise.all([
        fetch(`${API_BASE_URL}/admin/teams`),
        fetch(`${API_BASE_URL}/admin/users`),
        fetch(`${API_BASE_URL}/admin/grades`)
      ]);
      const tData = await tRes.json();
      const uData = await uRes.json();
      const gData = await gRes.json();

      setTeams(tData?.teams || []);
      setAccounts(uData?.users || []);
      setGrades(Array.isArray(gData) ? gData : []);
    } catch (e) { console.error("데이터 로드 실패:", e); } 
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  // 🟢 [추가] "미배정" 팀을 자동으로 찾아서 맨 위로 올린 목록 (정렬 로직)
  const sortedTeamsForDropdown = useMemo(() => {
    return [...teams].sort((a, b) => {
      // '미배정'이라는 이름이 있으면 무조건 맨 앞으로 보냅니다.
      if (a.name === '미배정') return -1;
      if (b.name === '미배정') return 1;
      return a.name.localeCompare(b.name);
    });
  }, [teams]);

  // 🟢 [추가] 모달 열릴 때, '미배정' 팀 ID를 자동으로 기본값으로 설정
  useEffect(() => {
    if (isAddingUser && sortedTeamsForDropdown.length > 0) {
      // 목록의 첫 번째(미배정) 팀 ID를 기본값으로 넣음 -> 이러면 team_id가 절대 null이 안됨!
      setNewUser(prev => ({ ...prev, team_id: sortedTeamsForDropdown[0].id }));
    }
  }, [isAddingUser, sortedTeamsForDropdown]);

  // 유저 선택 시 임시 수정 상태(editingUser) 동기화
  useEffect(() => {
    if (selectedUserIds.size === 1) {
      const firstId = Array.from(selectedUserIds)[0];
      const user = accounts.find(a => a.id === firstId);
      setEditingUser(user ? { ...user } : null);
    } else {
      setEditingUser(null);
    }
  }, [selectedUserIds, accounts]);

  // =============================================================================
  // 🎖️ [SECTION 4] 직급 관리 로직 (Grade Management)
  // =============================================================================
  const handleAddGrade = async () => {
    if (!newGradeName.trim()) return;
    try {
      const res = await fetch(`${API_BASE_URL}/admin/grades`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newGradeName.trim() })
      });
      if (res.ok) { setNewGradeName(''); loadData(); }
    } catch (e) { alert("추가 실패"); }
  };

  const handleDeleteGrade = async (gradeName: string) => {
    if (!window.confirm(`'${gradeName}' 직급을 삭제하시겠습니까?\n삭제 시 해당 직급을 가진 직원은 즉시 '미배정(-)'으로 변경됩니다.`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/admin/grades/${gradeName}`, { method: 'DELETE' });
      if (res.ok) {
        alert("삭제 및 미배정 처리가 완료되었습니다.");
        loadData(); // 🔄 즉시 전체 리로드를 통해 명단까지 최신화
      }
    } catch (e) { alert("직급 삭제 실패"); }
  };

  // =============================================================================
  // 🏢 [SECTION 5] 부서 관리 로직 (Team Management) - 신규 분리 및 수정
  // =============================================================================
  
  // 1. 부서 추가
  const handleAddTeam = async () => {
    if (!newTeamName.trim()) return;
    try {
      const res = await fetch(`${API_BASE_URL}/admin/teams`, { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' }, 
        body: JSON.stringify({ name: newTeamName.trim() }) 
      });
      if (res.ok) { setNewTeamName(''); setIsAddingTeam(false); loadData(); }
      else { alert("부서 추가에 실패했습니다."); }
    } catch (e) { alert("서버 연결 오류"); }
  };
  
  // 2. 부서 삭제
  const handleDeleteTeam = async (id: number) => {
    if (window.confirm("부서를 삭제하시겠습니까? 부서원은 미배정 상태가 됩니다.")) { 
      try {
        const res = await fetch(`${API_BASE_URL}/admin/teams/${id}`, { method: 'DELETE' }); 
        if (res.ok) { setSelectedTeamId('all'); loadData(); }
      } catch (e) { alert("부서 삭제 실패"); }
    }
  };

  // 3. 부서 이동 (Drag & Drop)
  const handleDropOnTeam = async (e: React.DragEvent, targetTeamId: number | null) => {
    e.preventDefault();
    const targets = selectedUserIds.size > 0 ? selectedUserIds : new Set([draggedUserId!]);
    if (targets.size === 0 || selectedTeamId === targetTeamId) return;
    try {
      await Promise.all(Array.from(targets).map(id => 
        fetch(`${API_BASE_URL}/admin/users/${id}`, { 
          method: 'PUT', 
          headers: { 'Content-Type': 'application/json' }, 
          body: JSON.stringify({ team_id: targetTeamId }) 
        })
      ));
      loadData();
    } catch { alert("이동 실패"); }
    setSelectedUserIds(new Set()); setDraggedUserId(null);
  };

  // =============================================================================
  // 💾 [SECTION 6] 데이터 가공 (Filtering & Grouping)
  // =============================================================================
  const allFilteredAccounts = useMemo(() => {
    return accounts.filter(a => a.name.includes(topSearchTerm) || a.username.includes(topSearchTerm) || (a.team_name || '').includes(topSearchTerm));
  }, [accounts, topSearchTerm]);

  const teamCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    teams.forEach(t => counts[t.id] = 0);
    counts['unassigned'] = 0; counts['all'] = accounts.length;
    accounts.forEach(acc => {
      if (acc.team_id && counts[acc.team_id] !== undefined) counts[acc.team_id]++;
      else counts['unassigned']++;
    });
    return counts;
  }, [teams, accounts]);

  const currentTeamMembers = useMemo(() => {
    let members = accounts;
    if (selectedTeamId === 'unassigned') members = accounts.filter(a => !a.team_id);
    else if (typeof selectedTeamId === 'number') members = accounts.filter(a => a.team_id === selectedTeamId);
    if (bottomSearchTerm) { members = members.filter(a => a.name.includes(bottomSearchTerm) || a.username.includes(bottomSearchTerm)); }
    return members;
  }, [accounts, selectedTeamId, bottomSearchTerm]);

  // =============================================================================
  // 🖱️ [SECTION 7] 이벤트 핸들러 (Event Handlers)
  // =============================================================================
  const handleTeamSelect = (id: number | 'unassigned' | 'all') => { setSelectedTeamId(id); setSelectedUserIds(new Set()); setBottomSearchTerm(''); };
  const handleUserClick = (e: React.MouseEvent, userId: number) => {
    if (e.ctrlKey || e.metaKey) {
      const newSet = new Set(selectedUserIds);
      if (newSet.has(userId)) newSet.delete(userId); else newSet.add(userId);
      setSelectedUserIds(newSet);
    } else { setSelectedUserIds(new Set([userId])); }
  };
  const handleDragStart = (e: React.DragEvent, userId: number) => { if (!selectedUserIds.has(userId)) setSelectedUserIds(new Set([userId])); setDraggedUserId(userId); e.dataTransfer.effectAllowed = "move"; };
  const handleDragOver = (e: React.DragEvent) => e.preventDefault();

  // =============================================================================
  // 💾 [SECTION 8] 데이터 저장 및 계정 관리 (CRUD)
  // =============================================================================
  
  // 1. 계정 생성
  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.username || !newUser.password) {
      alert("이름, 아이디, 비밀번호는 필수 입력 항목입니다.");
      return;
    }

    // 🟢 [안전장치] 혹시라도 team_id가 null이면 첫 번째 팀(미배정)으로 강제 할당
    let finalTeamId = newUser.team_id;
    if (!finalTeamId && sortedTeamsForDropdown.length > 0) {
        finalTeamId = sortedTeamsForDropdown[0].id;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newUser, team_id: finalTeamId }) // 강제 할당한 ID 전송
      });
      if (res.ok) {
        alert("새 계정이 생성되었습니다.");
        setIsAddingUser(false);
        setNewUser({ name: '', username: '', email: '', password: '', team_id: null, grade: '사원', role: 'user' });
        loadData();
      } else {
        const err = await res.json();
        alert(err.detail || "생성 실패");
      }
    } catch (e) { alert("서버 연결 오류"); }
  };

  // 2. 계정 수정 보관
  const handleSaveUser = async () => {
    if (!editingUser) return;
    try {
      const payload: any = { ...editingUser };
      if (newPassword.trim()) payload.password = newPassword.trim();
      const res = await fetch(`${API_BASE_URL}/admin/users/${editingUser.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if(res.ok) { alert("정보가 수정되었습니다."); setNewPassword(''); loadData(); }
    } catch { alert("저장 실패"); }
  };

  // 3. 선택 계정 삭제 (일괄)
  const handleDeleteUsers = async () => {
    if (selectedUserIds.size === 0) return;
    if (window.confirm(`선택한 ${selectedUserIds.size}명의 계정을 정말로 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`)) {
      try { 
        await Promise.all(Array.from(selectedUserIds).map(id => 
          fetch(`${API_BASE_URL}/admin/users/${id}`, { method: 'DELETE' })
        )); 
        loadData(); 
        setSelectedUserIds(new Set()); 
      } catch { alert("삭제 실패"); }
    }
  };

  // =============================================================================
  // 🎨 [SECTION 9] 화면 렌더링 (UI Rendering)
  // =============================================================================
  
  // 🚨 [조건부 렌더링] 매트릭스 버튼을 눌렀을 때 보여줄 화면
  if (showMatrix) {
    return (
      <div className="h-[calc(100vh-80px)] flex flex-col gap-2 overflow-hidden bg-slate-50/50 p-2 relative font-sans animate-fade-in">
        <div className="shrink-0 flex items-center justify-between p-2">
          {/* 뒤로가기 버튼 */}
          <button 
            onClick={() => setShowMatrix(false)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-black text-slate-500 hover:text-slate-900 bg-white border border-slate-200 rounded-xl shadow-sm hover:bg-slate-50 transition-all"
          >
            <ArrowLeft size={16} /> 계정 관리로 돌아가기
          </button>
        </div>
        <div className="flex-1 overflow-hidden rounded-[24px]">
          <AdminSettings />
        </div>
      </div>
    );
  }

  // 👤 기본 계정 관리 화면
  return (
    <div className="h-[calc(100vh-100px)] flex flex-col gap-4 overflow-hidden bg-slate-50/50 p-2 relative">
      
      {/* 🎖️ 직급 관리 모달 */}
      {isManagingGrades && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl w-[320px] overflow-hidden border border-slate-200">
            <div className="p-4 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
              <h3 className="font-black text-slate-700 flex items-center gap-2 text-sm"><Briefcase size={16}/> 직급 목록 관리</h3>
              <button onClick={() => setIsManagingGrades(false)} className="p-1 hover:bg-slate-200 rounded text-slate-400"><X size={16}/></button>
            </div>
            <div className="p-4 h-[300px] overflow-y-auto space-y-2 bg-white">
              {grades.map((grade, idx) => (
                <div key={idx} className="flex justify-between items-center p-2.5 rounded-lg border border-slate-100 bg-slate-50 hover:border-red-200 group">
                  <span className="text-xs font-bold text-slate-700">{grade}</span>
                  <button onClick={() => handleDeleteGrade(grade)} className="text-[10px] font-black text-red-500 bg-red-50 px-2 py-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-500 hover:text-white transition-all">삭제</button>
                </div>
              ))}
            </div>
            <div className="p-3 border-t border-slate-100 bg-slate-50 flex gap-2">
              <input className="flex-1 text-xs px-2 py-1.5 border border-slate-200 rounded-lg outline-none focus:border-blue-300 bg-white" placeholder="새 직급명" value={newGradeName} onChange={(e) => setNewGradeName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAddGrade()} />
              <button onClick={handleAddGrade} className="bg-blue-600 text-white px-3 rounded-lg text-xs font-bold hover:bg-blue-700 shadow-sm">추가</button>
            </div>
          </div>
        </div>
      )}

      {/* 👤 계정 생성 모달 */}
      {isAddingUser && (
        <div className="absolute inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl w-[400px] overflow-hidden border border-slate-200 animate-fade-in-up">
            <div className="p-5 bg-slate-900 text-white flex justify-between items-center">
              <h3 className="font-black flex items-center gap-2 text-sm"><UserCog size={18}/> 신규 계정 생성</h3>
              <button onClick={() => setIsAddingUser(false)} className="text-slate-400 hover:text-white"><X size={20}/></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase">이름 *</label>
                  <input className="w-full p-2.5 border border-slate-200 rounded-xl text-xs font-bold outline-none focus:border-blue-500" placeholder="홍길동" value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase">아이디 *</label>
                  <input className="w-full p-2.5 border border-slate-200 rounded-xl text-xs font-bold outline-none focus:border-blue-500" placeholder="hgd123" value={newUser.username} onChange={e => setNewUser({...newUser, username: e.target.value})} />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase">비밀번호 *</label>
                <input type="password" dir="ltr" className="w-full p-2.5 border border-slate-200 rounded-xl text-xs font-bold outline-none focus:border-blue-500" placeholder="••••••••" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase">이메일</label>
                <input className="w-full p-2.5 border border-slate-200 rounded-xl text-xs font-bold outline-none focus:border-blue-500" placeholder="example@company.com" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase">부서</label>
                  <select 
                    className="w-full p-2.5 border border-slate-200 rounded-xl text-xs font-bold outline-none focus:border-blue-500" 
                    value={newUser.team_id || ''} 
                    onChange={e => setNewUser({...newUser, team_id: Number(e.target.value)})}
                  >
                    {sortedTeamsForDropdown.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase">직급</label>
                  <select className="w-full p-2.5 border border-slate-200 rounded-xl text-xs font-bold outline-none focus:border-blue-500" value={newUser.grade} onChange={e => setNewUser({...newUser, grade: e.target.value})}>
                    {grades.map((g, idx) => <option key={idx} value={g}>{g}</option>)}
                  </select>
                </div>
              </div>

              {/* 🚨 [수정] 리더 직급 삭제, 일반/관리자 2개만 남김 */}
              <div className="space-y-1 mt-2">
                <label className="text-[10px] font-black text-blue-500 uppercase">시스템 권한 (Role)</label>
                <select 
                  className="w-full p-2.5 border border-blue-200 bg-blue-50 rounded-xl text-xs font-black text-blue-800 outline-none focus:border-blue-500" 
                  value={newUser.role} 
                  onChange={e => setNewUser({...newUser, role: e.target.value})}
                >
                  <option value="user">일반 사용자 (전사 권한 매트릭스로 제어)</option>
                  <option value="admin">최고 관리자 (모든 권한 프리패스)</option>
                </select>
              </div>

            </div>
            <div className="p-4 bg-slate-50 border-t border-slate-100 flex gap-2">
              <button onClick={() => setIsAddingUser(false)} className="flex-1 py-3 border border-slate-200 rounded-xl text-xs font-bold text-slate-500 bg-white hover:bg-slate-100">취소</button>
              <button onClick={handleCreateUser} className="flex-1 py-3 bg-blue-600 text-white rounded-xl text-xs font-bold hover:bg-blue-700 shadow-lg shadow-blue-100">계정 생성하기</button>
            </div>
          </div>
        </div>
      )}

      {/* [상단] 전체 목록 (35%) */}
      <section className="h-[35%] shrink-0 bg-white rounded-[24px] border border-slate-200 shadow-sm flex flex-col overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/30">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-lg text-blue-600"><ShieldCheck size={20}/></div>
            <div><h2 className="text-sm font-black text-slate-800">전체 계정 현황</h2><span className="text-[10px] text-slate-400 font-bold">Admin Mode</span></div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl border border-slate-200 shadow-inner">
              <Search size={14} className="text-slate-400" />
              <input type="text" placeholder="검색..." className="text-xs outline-none font-bold w-48" value={topSearchTerm} onChange={e => setTopSearchTerm(e.target.value)} />
            </div>
            {/* 🚨 [삭제] 전사 권한 매트릭스 이동 버튼이 여기서 삭제되고 하단 작업 공간으로 이동했습니다. */}
          </div>
        </div>
        <div className="flex-1 overflow-auto scrollbar-thin">
          <table className="w-full text-left border-collapse table-fixed">
            <thead className="sticky top-0 bg-slate-900 text-slate-400 text-[10px] font-black uppercase tracking-widest z-10">
              <tr>
                <th className="px-4 py-2 w-[15%]">이름</th>
                <th className="px-4 py-2 w-[15%]">부서</th>
                <th className="px-4 py-2 w-[10%]">직급</th>
                <th className="px-4 py-2 w-[10%] text-blue-300">권한</th> {/* 🚨 신규추가 */}
                <th className="px-4 py-2 w-[15%]">아이디</th>
                <th className="px-4 py-2 w-[20%]">이메일</th>
                <th className="px-4 py-2 w-[15%]">연락처</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {allFilteredAccounts.map(a => (
                <tr key={a.id} className="text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-2 text-slate-900">{a.name}</td>
                  <td className="px-4 py-2 text-blue-600">{a.team_name || '미배정'}</td>
                  <td className="px-4 py-2 text-slate-500">{a.grade || '-'}</td>
                  
                  {/* 🚨 [신규 추가] 권한 뱃지 렌더링 (리더 제외) */}
                  <td className="px-4 py-2">
                    <span className={`px-2 py-1 rounded-md text-[10px] font-black tracking-tight ${
                      a.role === 'admin' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {a.role === 'admin' ? '관리자' : '일반'}
                    </span>
                  </td>

                  <td className="px-4 py-2 text-slate-400">{a.username}</td>
                  <td className="px-4 py-2 text-slate-400 truncate">{a.email}</td>
                  <td className="px-4 py-2 text-slate-400">{a.phone || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* [하단] 3단 작업 공간 (65%) */}
      <div className="flex-1 flex gap-4 overflow-hidden min-h-0">
        
        {/* 좌측 조직도 (w-1/5) */}
        <section className="w-1/5 min-w-[220px] flex flex-col bg-white rounded-[24px] border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
            <h3 className="text-xs font-black text-slate-500 uppercase flex items-center gap-2"><Building2 size={14}/> 조직도</h3>
            <div className="flex gap-1">
              <button onClick={() => setIsManagingGrades(true)} className="flex items-center gap-1 p-1 bg-white border border-slate-200 rounded text-slate-500 hover:text-blue-600 transition-colors font-bold text-[9px] px-2 shadow-sm"><Settings size={12} /> 직급 관리</button>
              <button onClick={() => setIsAddingTeam(!isAddingTeam)} className="flex items-center gap-1 bg-white border border-slate-200 px-2 py-1 rounded text-[9px] font-bold text-slate-600 hover:bg-blue-50 transition-all shadow-sm"><Plus size={10}/> 부서 추가</button>
            </div>
          </div>
          {isAddingTeam && (
            <div className="p-3 bg-blue-50 border-b border-blue-100 animate-fade-in-down">
              <input className="w-full text-xs p-2.5 rounded-lg border border-blue-200 outline-none mb-2 bg-white" placeholder="부서명" value={newTeamName} onChange={e=>setNewTeamName(e.target.value)} autoFocus />
              <div className="flex gap-2"><button onClick={handleAddTeam} className="flex-1 bg-blue-600 text-white py-1.5 rounded-lg text-xs font-bold shadow-md shadow-blue-200">추가</button><button onClick={() => setIsAddingTeam(false)} className="flex-1 bg-white text-slate-500 border border-slate-200 py-1.5 rounded-lg text-xs font-bold">취소</button></div>
            </div>
          )}
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            <div onClick={() => handleTeamSelect('all')} className={`flex items-center justify-between px-3 py-2.5 rounded-xl cursor-pointer transition-all ${selectedTeamId === 'all' ? 'bg-slate-800 text-white shadow-md' : 'text-slate-600 hover:bg-slate-100'}`}><div className="flex items-center gap-2"><Users size={14} /><span className="text-sm font-bold">전체 인원</span></div><span className="text-[10px] font-bold opacity-70">{teamCounts['all']}</span></div>
            <div className="my-2 border-t border-slate-100"></div>
            {teams.map(team => (
              <div key={team.id} onClick={() => handleTeamSelect(team.id)} onDragOver={handleDragOver} onDrop={(e) => handleDropOnTeam(e, team.id)} className={`group flex items-center justify-between px-3 py-2.5 rounded-xl cursor-pointer transition-all ${selectedTeamId === team.id ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'text-slate-600 hover:bg-slate-50 border border-transparent'}`}>
                <div className="flex items-center gap-2"><Building2 size={14} className={selectedTeamId === team.id ? 'text-blue-500' : 'text-slate-400'} /><span className="text-sm font-bold">{team.name}</span></div>
                <div className="flex items-center gap-2"><span className={`text-[10px] font-bold px-1.5 rounded-md ${selectedTeamId === team.id ? 'bg-blue-200 text-blue-800' : 'bg-slate-100 text-slate-400'}`}>{teamCounts[team.id] || 0}</span><button onClick={(e) => { e.stopPropagation(); handleDeleteTeam(team.id); }} className="opacity-0 group-hover:opacity-100 text-[10px] font-bold text-red-400 hover:text-red-600 hover:bg-red-50 px-1.5 py-0.5 rounded transition-all">삭제</button></div>
              </div>
            ))}
          </div>
        </section>

        {/* 중앙 명단 목록 (flex-1) */}
        <section className="flex-1 flex flex-col bg-white rounded-[24px] border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/30">
            <div className="flex items-center gap-2"><h3 className="text-lg font-black text-slate-800">{selectedTeamId === 'all' ? '전체 인원' : selectedTeamId === 'unassigned' ? '미배정 인원' : teams.find(t => t.id === selectedTeamId)?.name}</h3><span className="text-xs font-medium text-slate-400">({currentTeamMembers.length}명)</span></div>
            <div className="flex gap-2 items-center">
                {/* 🚨 [이동 완료] 전사 권한 매트릭스 이동 버튼이 계정 생성 버튼 옆(왼쪽)으로 이동했습니다. */}
                <button 
                  onClick={() => setShowMatrix(true)} 
                  className="px-4 py-1.5 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-700 transition-all shadow-md shadow-indigo-200 hover:-translate-y-0.5 flex items-center gap-1.5"
                >
                  <Settings size={14} /> 전사 권한 매트릭스 열기
                </button>
                {/* 계정 생성 버튼 */}
                <button 
                  onClick={() => setIsAddingUser(true)}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 transition-all flex items-center gap-1 shadow-md shadow-blue-100"
                >
                  <Plus size={14} /> 계정 생성
                </button>
                {/* 선택 삭제 버튼 */}
                <button 
                  onClick={handleDeleteUsers} 
                  disabled={selectedUserIds.size === 0} 
                  className="px-3 py-1.5 bg-white border border-red-200 text-red-500 rounded-lg text-xs font-bold hover:bg-red-500 hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <Trash2 size={14} /> {selectedUserIds.size > 0 ? `${selectedUserIds.size}명 삭제` : '선택 삭제'}
                </button>
                <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14}/><input className="pl-9 pr-4 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold w-40 outline-none focus:border-blue-400" placeholder="명단 내 검색" value={bottomSearchTerm} onChange={e=>setBottomSearchTerm(e.target.value)}/></div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2 bg-slate-50/30 scrollbar-thin">
            {currentTeamMembers.map(user => (
              <div key={user.id} draggable onDragStart={(e) => handleDragStart(e, user.id)} onClick={(e) => handleUserClick(e, user.id)} className={`flex items-center justify-between p-3 rounded-xl cursor-pointer border transition-all select-none ${selectedUserIds.has(user.id) ? 'bg-blue-50 border-blue-200 shadow-sm z-10' : 'bg-white border-transparent hover:border-slate-200'}`}>
                <div className="flex items-center gap-3"><GripVertical size={14} className="text-slate-300 cursor-grab"/><div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black ${selectedUserIds.has(user.id) ? 'bg-blue-200 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>{user.name.slice(0,1)}</div><div><div className="text-sm font-bold text-slate-800 flex items-center gap-2">{user.name} <span className="text-[10px] text-slate-400 font-normal bg-slate-100 px-1.5 rounded">{user.grade}</span></div><div className="text-[10px] text-slate-400 font-mono">{user.email}</div></div></div>
                <ChevronRight size={14} className={`text-slate-300 transition-transform ${selectedUserIds.has(user.id) ? 'translate-x-1 text-blue-500' : ''}`}/>
              </div>
            ))}
          </div>
        </section>

        {/* 우측 상세 패널 (w-1/4) */}
        <section className="w-1/4 min-w-[300px] flex flex-col bg-white rounded-[24px] border border-slate-200 shadow-sm overflow-hidden shrink-0">
          {selectedUserIds.size === 1 && editingUser ? (
            <div className="h-full flex flex-col bg-white">
              <div className="flex items-center gap-4 p-5 border-b border-slate-100 bg-slate-50/50"><div className="w-12 h-12 rounded-xl bg-slate-800 text-white flex items-center justify-center text-lg font-black shadow-md shrink-0">{editingUser.name.slice(0,1)}</div><div className="flex-1 min-w-0"><h2 className="text-lg font-black text-slate-800 truncate">{editingUser.name}</h2><div className="flex items-center gap-1.5 text-xs text-slate-500"><BadgeCheck size={12} className="text-blue-500"/><span className="truncate">{editingUser.username}</span></div></div><button onClick={() => { if(window.confirm(`'${editingUser.name}' 계정을 삭제하시겠습니까?`)) fetch(`${API_BASE_URL}/admin/users/${editingUser.id}`, {method: 'DELETE'}).then(() => { loadData(); setSelectedUserIds(new Set()); }); }} className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={16}/></button></div>
              <div className="flex-1 overflow-y-auto p-5 space-y-6 scrollbar-thin">
                <div className="space-y-3"><h4 className="text-[10px] font-black text-slate-400 uppercase tracking-wider flex items-center gap-1"><UserCog size={12}/> 계정 정보</h4><div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><label className="text-[10px] font-bold text-slate-500">이름</label><input className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold outline-none" value={editingUser.name} onChange={e => setEditingUser({...editingUser, name: e.target.value})}/></div>
                  <div className="space-y-1"><label className="text-[10px] font-bold text-slate-500">아이디</label><input disabled className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold outline-none text-slate-400 cursor-not-allowed" value={editingUser.username} /></div>
                </div></div>
                <div className="space-y-3"><h4 className="text-[10px] font-black text-slate-400 uppercase tracking-wider flex items-center gap-1"><Briefcase size={12}/> 소속 및 직급</h4><div className="grid grid-cols-2 gap-3">
                  <select className="col-span-1 p-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-blue-100" value={editingUser.team_id || ''} onChange={e => setEditingUser({...editingUser, team_id: Number(e.target.value)})}> <option value="">미배정</option>{teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select>
                  <select className="col-span-1 p-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-blue-100" value={editingUser.grade || '사원'} onChange={e => setEditingUser({...editingUser, grade: e.target.value})}>
                    {grades.map((g, idx) => <option key={g} value={g}>{g}</option>)}
                  </select>
                  {/* 🚨 [수정] 리더 직급 삭제, 일반/관리자 2개만 남김 */}
                  <select 
                    className="col-span-2 p-2.5 bg-blue-50 border border-blue-200 rounded-xl text-xs font-black text-blue-800 outline-none focus:ring-2 focus:ring-blue-400" 
                    value={editingUser.role || 'user'} 
                    onChange={e => setEditingUser({...editingUser, role: e.target.value})}
                  >
                    <option value="user">일반 사용자 (전사 권한 매트릭스로 제어)</option>
                    <option value="admin">최고 관리자 (모든 권한 프리패스)</option>
                  </select>
                </div></div>
                <div className="space-y-3"><h4 className="text-[10px] font-black text-slate-400 uppercase tracking-wider flex items-center gap-1"><Mail size={12}/> 연락처</h4><div className="space-y-2"><input className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-medium outline-none" value={editingUser.email} onChange={e => setEditingUser({...editingUser, email: e.target.value})}/><input className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-medium outline-none" value={editingUser.phone || ''} placeholder="전화번호" onChange={e => setEditingUser({...editingUser, phone: e.target.value})}/></div></div>
                <div className="space-y-3"><h4 className="text-[10px] font-black text-slate-400 uppercase tracking-wider flex items-center gap-1"><Lock size={12}/> 보안</h4><input type="password" placeholder="새 비밀번호" className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-medium outline-none" value={newPassword} onChange={e => setNewPassword(e.target.value)}/></div>
              </div>
              <div className="p-4 border-t border-slate-100 bg-white"><button onClick={handleSaveUser} className="w-full bg-slate-900 text-white py-3 rounded-xl font-bold text-sm hover:bg-blue-600 transition-all shadow-lg flex items-center justify-center gap-2"><Save size={16}/> 변경사항 저장</button></div>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-slate-400 p-6 text-center bg-slate-50/30"><UserCog size={32} className="mb-4 opacity-30"/><p className="text-xs">직원을 선택하면 상세 정보가 나타납니다.</p></div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AccountManagement;